import React, { Component } from 'react';
import { projectsRef } from '../config/firebase';
import { onValue, update, set, child, remove } from 'firebase/database';
import { showToast } from '../actions/toastBus';
import PageLoader from './PageLoader';
import {
    LuPlus,
    LuSave,
    LuTrash2,
    LuX,
    LuSparkles,
    LuPencilLine,
    LuTag,
    LuCalendar
} from 'react-icons/lu';
import '../stylesheet/Projects.css';
import '../stylesheet/style.css';

export default class Projects extends Component {

    constructor(props) {
        super(props);
        this.state = {
            projects: [],
            contentLoading: true,
            isLoggedIn: false,
            newProject: { name: '', org: '', date: '', desc: '' },
            // null = the popup is adding a new project; otherwise the id
            // (real Firebase index) of the project being edited.
            editingId: null,
            modalOpen: false,
            modalClosing: false
        };
    };

    componentDidMount() {
        this.setState({ isLoggedIn: localStorage.getItem('adminLoggedIn') });
        onValue(projectsRef, (snapshot) => {
            const data = snapshot.val();
            const projects = Array.isArray(data)
                ? data
                : (data ? Object.values(data) : []);
            this.setState({
                projects,
                contentLoading: false
            });
        });
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        window.clearTimeout(this._modalTimer);
    }

    handleKeyDown = (event) => {
        if (event.key === 'Escape' && this.state.modalOpen) {
            this.closeModal();
        }
    }

    // Same popup handles add and edit: null editingId = blank form for a
    // new project, otherwise the form is pre-filled from that card and
    // saves back to its own Firebase key instead of creating a new one.
    openAddModal = () => {
        this.setState({
            editingId: null,
            newProject: { name: '', org: '', date: '', desc: '' },
            modalOpen: true,
            modalClosing: false
        });
    }

    openEditModal = (id, project) => {
        this.setState({
            editingId: id,
            newProject: {
                name: project.name || '',
                org: project.org || '',
                date: project.date || '',
                desc: project.desc || ''
            },
            modalOpen: true,
            modalClosing: false
        });
    }

    closeModal = () => {
        // Match the exit-animation timing (see .project-modal-* in
        // Projects.css) before actually unmounting.
        this.setState({ modalClosing: true });
        window.clearTimeout(this._modalTimer);
        this._modalTimer = window.setTimeout(() => {
            this.setState({ modalOpen: false, modalClosing: false, editingId: null });
        }, 320);
    }

    handleNewProjectChange = (field, value) => {
        this.setState((prev) => ({
            newProject: { ...prev.newProject, [field]: value }
        }));
    }

    saveProject = () => {
        const { name, org, date, desc } = this.state.newProject;
        if (!name || !desc) return showToast('Fill in every field before saving.', 'error');
        update(projectsRef, {
            [this.state.editingId]: { name, date: date || '', org: org || '', desc }
        })
            .then(() => {
                this.closeModal();
                showToast('Project updated', 'success');
            })
            .catch((error) => {
                console.error('Error updating project:', error);
                showToast('Could not update this project - please try again.', 'error');
            });
    }

    addNewProject = () => {
        const { name, org, date, desc } = this.state.newProject;
        if (!name || !desc) return showToast('Fill in every field before adding.', 'error');

        const id = this.state.projects.length.toString(); // Firebase keys must be strings
        set(child(projectsRef, id), { name, org: org || '', date: date || '', desc })
            .then(() => {
                this.setState({ newProject: { name: '', org: '', date: '', desc: '' } });
                this.closeModal();
                showToast('New project added', 'success');
            })
            .catch((error) => {
                console.error("Error saving project:", error);
                showToast('Could not add this project - please try again.', 'error');
            });
    }

    deleteProject = () => {
        if (this.state.editingId === null) return;
        if (!window.confirm("Delete this project? This can't be undone.")) return;

        remove(child(projectsRef, this.state.editingId.toString()))
            .then(() => {
                this.closeModal();
                showToast('Project deleted', 'success');
            })
            .catch((error) => {
                console.error("Error deleting project:", error);
                showToast('Could not delete this project - please try again.', 'error');
            });
    }

    handleModalSubmit = () => {
        if (this.state.editingId !== null) {
            this.saveProject();
        } else {
            this.addNewProject();
        }
    }

    // Preserve each project's real Firebase index as `id` before reversing
    // for display, otherwise a save after reversal would write to the
    // wrong record.
    renderProjectGrid = (items) => {
        const ordered = items
            .map((item, id) => ({ item, id }))
            .filter((entry) => entry.item)
            .slice()
            .reverse();

        return (
            <div className="projects-grid">
                {ordered.map(({ item, id }) => (
                    this.state.isLoggedIn ? (
                        <div
                            className="project-card project-card-clickable"
                            key={id}
                            onClick={() => this.openEditModal(id, item)}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), this.openEditModal(id, item))}
                            role="button"
                            tabIndex={0}
                            aria-label={`Edit ${item.name}`}
                        >
                            <div className="project-body">
                                <div className="project-top">
                                    <h3>{item.name}</h3>
                                    {item.date && <span className="project-date">{item.date}</span>}
                                </div>
                                {item.org && <span className="project-org">{item.org}</span>}
                                <p>{item.desc}</p>
                                <span className="project-edit-hint"><LuPencilLine /> Edit</span>
                            </div>
                        </div>
                    ) : (
                        <div className="project-card" key={id}>
                            <div className="project-body">
                                <div className="project-top">
                                    <h3>{item.name}</h3>
                                    {item.date && <span className="project-date">{item.date}</span>}
                                </div>
                                {item.org && <span className="project-org">{item.org}</span>}
                                <p>{item.desc}</p>
                            </div>
                        </div>
                    )
                ))}
            </div>
        );
    };

    render() {
        const { newProject, isLoggedIn, modalOpen, modalClosing, editingId } = this.state;

        return (
            <div className='base'>
                <div className='main-content-outer'>
                <div className="projects-page">
                    <h1 className="sr-only">Projects</h1>
                    <span className="projects-watermark" aria-hidden="true">Projects</span>
                    {this.state.contentLoading && <PageLoader />}

                    {this.renderProjectGrid(this.state.projects)}

                    {isLoggedIn && (
                        <button
                            type="button"
                            className="project-fab"
                            onClick={this.openAddModal}
                            aria-label="Add new project"
                        >
                            <LuPlus />
                        </button>
                    )}

                    {modalOpen && (
                        <div
                            className={`project-modal-backdrop${modalClosing ? ' is-closing' : ''}`}
                            onClick={this.closeModal}
                        >
                            <div className="project-modal-panel" onClick={(e) => e.stopPropagation()}>
                                <button
                                    type="button"
                                    className="project-modal-close"
                                    onClick={this.closeModal}
                                    aria-label="Close"
                                >
                                    <LuX />
                                </button>
                                <span className="project-add-eyebrow">
                                    {editingId !== null ? <><LuPencilLine /> Editing</> : <><LuSparkles /> Admin</>}
                                </span>
                                <h3 className="project-add-heading">
                                    {editingId !== null ? 'Edit project' : 'Add a new project'}
                                </h3>
                                <label className="project-field">
                                    <span className="project-field-label">Project name</span>
                                    <input
                                        type="text"
                                        placeholder="Project name"
                                        value={newProject.name}
                                        onChange={(e) => this.handleNewProjectChange('name', e.target.value)}
                                        className="project-field-input"
                                        autoFocus
                                    />
                                </label>
                                <div className="project-field-row">
                                    <label className="project-field">
                                        <span className="project-field-label"><LuTag /> Org / stack</span>
                                        <input
                                            type="text"
                                            placeholder="e.g. React, Node"
                                            value={newProject.org}
                                            onChange={(e) => this.handleNewProjectChange('org', e.target.value)}
                                            className="project-field-input"
                                        />
                                    </label>
                                    <label className="project-field">
                                        <span className="project-field-label"><LuCalendar /> Date</span>
                                        <input
                                            type="text"
                                            placeholder="e.g. Mar 2024"
                                            value={newProject.date}
                                            onChange={(e) => this.handleNewProjectChange('date', e.target.value)}
                                            className="project-field-input"
                                        />
                                    </label>
                                </div>
                                <label className="project-field">
                                    <span className="project-field-label">Description</span>
                                    <textarea
                                        rows={4}
                                        placeholder="Description"
                                        value={newProject.desc}
                                        onChange={(e) => this.handleNewProjectChange('desc', e.target.value)}
                                        className="project-field-input project-field-textarea"
                                    />
                                </label>
                                <div className="project-modal-actions">
                                    <button
                                        onClick={this.handleModalSubmit}
                                        className="app-form-button project-save-cta"
                                    >
                                        {editingId !== null ? <><LuSave /> Save changes</> : <><LuPlus /> Add project</>}
                                    </button>
                                    {editingId !== null && (
                                        <button
                                            type="button"
                                            onClick={this.deleteProject}
                                            className="project-delete-cta"
                                        >
                                            <LuTrash2 /> Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                </div>
            </div>
        )
    }
}
